import { gsap, Linear } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import VimeoSection from "~/js/common/vimeo-section";
import Common from "~/js/common/common";
import store from "~/js/store/store";
import _each from "lodash/each";
import _pullAt from "lodash/pullAt";
import _debounce from "lodash/debounce";
import _throttle from "lodash/throttle";
import fullpage from 'fullpage.js';

export default class PinSection {
  constructor(options) {
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(ScrollToPlugin);
    this.scrollTween = null;
    this.multiImageTween = null;
    this.autoScrollTween = null;
    this.resizeFlg = false;
    this.triggers = [];

    this.isScrolling = false;
    this.currentIndex = 0;
    this.sectionLength = 0;
    this.options = {};
    this.isPlay = true;
    this.interval = null;
    this.pausedTime = 0;
    this.sectionStartTime = 0;
    this.fullpageInterval = null;
    this.playPauseButtonTarget = null
    if (options) {
      this.setupOptions(options);
    }

    this.isPanning = false;
    this.startTouchY = 0;
    this.scrollY = 0;
    this.setupSections();

    this.setupFullPage()
    this.setupScrollTriggers();

    this.vimeoSection = new VimeoSection(false);
    this.playPauseButton = document.querySelector(".js-play-pause-button");

    this.playPauseButton?.addEventListener("click", this.setupPlayPauseButton.bind(this))
    this.playPauseButton?.addEventListener("blur", (e) => {
      if (!this.playPauseButtonTarget) return

      e.preventDefault()

      this.playPauseButtonTarget.focus()
    })

    document.addEventListener('pauseMultiImage', (evt) => {
      this.multiImageTween?.pause()
    })
    document.addEventListener('playMultiImage', () => {
      this.multiImageTween?.play()
    })

    const sections = this.getFpSections();
    sections.forEach((section, index) => {
      const focusableEls = section.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), details, [tabindex]:not([tabindex="-1"])')
      const lastFocusableEl = focusableEls.length ? focusableEls[focusableEls.length - 1] : null
      const vimeoSections = section.querySelectorAll(".js-vimeo-section");

      if (section && vimeoSections.length && store.state.app.isHome) {
        vimeoSections.forEach((_vimeoSection) => {
          const player = this.vimeoSection.getVimeoPlayer(_vimeoSection);
          const parrentSection = _vimeoSection?.closest(".js-section-background");

          const btn = parrentSection?.querySelector(".js-btn-play-pause");
          if (btn && player) {
            btn.addEventListener("click", (e) => {
              if (btn.hasAttribute('data-is-pressing')) return

              e.preventDefault();

              btn.setAttribute('data-is-pressing', '')

              requestAnimationFrame(() => {
                const pressed = e.target.getAttribute('aria-pressed')
                if (pressed === 'false') {
                  player?.pause();
                  this.multiImageTween?.pause();
                } else {
                  player?.play();
                  this.multiImageTween?.play()
                }

                btn.setAttribute('aria-pressed', pressed === 'false')
                btn.innerHTML = pressed === 'false' ? 'Play Video' : 'Pause Video'

                btn.removeAttribute('data-is-pressing')
              })
            });
          }
        })
      }

      const pauseMultiimage = section.querySelector(".js-multiimage-pause");
      if (pauseMultiimage) {
        pauseMultiimage.addEventListener("click", (e) => {
          e.preventDefault();
          const pressed = e.target.getAttribute('aria-pressed')
          if (pressed === 'false') {
            this.multiImageTween?.pause();
            section.classList.add("paused-slider");
          } else {
            this.multiImageTween?.play()
            section.classList.remove("paused-slider");
          }

          pauseMultiimage.setAttribute('aria-pressed', pressed !== 'true')
          pauseMultiimage.innerHTML = pressed === 'false' ? 'Play Slide' : 'Pause Slide'
        });
      }

      if (lastFocusableEl) {
        lastFocusableEl.addEventListener('blur', (e) => {
          if (lastFocusableEl.hasAttribute('data-is-tabbed-away')) {
            lastFocusableEl.removeAttribute('data-is-tabbed-away')
            return
          }

          e.preventDefault()

          lastFocusableEl.setAttribute('data-is-tabbed-away', '')

          this.playPauseButtonTarget = lastFocusableEl
          this.playPauseButton?.focus()
        })
      }
    })
  }

  setupPlayPauseButton() {
    this.isPlay = !this.isPlay;
    this.playPauseButton?.classList.toggle('is-pause', !this.isPlay);
    this.playPauseButton?.setAttribute('aria-label', this.isPlay ? 'Pause Slide Autoplay' : 'Play Slide Autoplay');

    if (this.isPlay) {
      this.sectionStartTime = Date.now() - this.pausedTime;
      this.startAutoScroll();
    } else {
      this.pausedTime = Date.now() - this.sectionStartTime;
      clearInterval(this.interval);
      clearInterval(this.fullpageInterval);
    }
  }

  startAutoScroll() {
    const time = 10000;
    clearInterval(this.interval);
    clearInterval(this.fullpageInterval);  // Clear any existing intervals

    this.interval = setInterval(() => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - this.sectionStartTime;
      const progress = Math.min(elapsedTime / time, 1);

      store.dispatch("pinSectionAutoplayTime", {
        time: progress
      });

      if (!this.isPlay) {
        clearInterval(this.interval);
        return;
      }

      if (elapsedTime >= time) {
        this.fullpage.moveSectionDown();
        this.sectionStartTime = Date.now();
        this.pausedTime = 0;
        this.startAutoScroll();
      }
    }, 100);
  }

  setupFullPage() {
    store.dispatch("init");

    const home = document.getElementById("home");

    this.fullpage = new fullpage(home ? "#home .home-wrapper" : "#main", {
      sectionSelector: home ? '.js-section:not(.pinfooter__brands,.pinfooter__otherlinks), .pinfooter' : '.shopify-section',
      keyboardScrolling: true,
      recordHistory: true,
      afterLoad: (origin, destination, direction, trigger) => {
        location.hash = "section" + destination.index;
        store.dispatch('beforeScroll', {
          prev: origin.index,
          next: destination.index,
          section: destination.item?.querySelector('.js-section')
        })

        store.dispatch('afterScroll', {
          y: window.scrollY
        })

        const sections = this.getFpSections();

        sections.forEach((section, index) => {
            section.setAttribute("role", "group")
            section.setAttribute("aria-label",`Slide ${index + 1} of ${sections.length}`)

          if (index === destination.index) {
            section.setAttribute('aria-hidden', 'false')

            // Set tabindex to 0 with all child elements can be focused
            section.querySelectorAll('a, button, input, textarea, select, details, summary, [tabindex]').forEach(child => {
              child.setAttribute('tabindex', '0')
            })
          } else {
            section.setAttribute('aria-hidden', 'true')

            // Set tabindex to -1 with all child elements can be focused
            section.querySelectorAll('a, button, input, textarea, select, details, summary, [tabindex]').forEach(child => {
              child.setAttribute('tabindex', '-1')
            })
          }
        })

        if (home) {
          this.sectionStartTime = Date.now();
          this.pausedTime = 0;

          home.setAttribute('data-current-index', destination.index)

          if (this.isPlay) {
            this.startAutoScroll();
          }
        }
      },
      beforeLeave: (origin, destination, direction) => {
        console.log('beforeLeave')
        const sections = this.getSections();

        _each(sections, (elm, index) => {
          if (index === destination.index) {
            this.photoSectionPlay(elm);
            this.vimeoSectionPlay(elm);
          } else {
            this.vimeoSectionPause(elm);
          }
        })
      }
    })

    this.unsubscribe = store.subscribe((mutation, state) => {
      if (mutation.type === 'changePinScroll') {
        let _to = state.app.pinSection.to;

        this.fullpage.moveTo(_to + 1)
      } else if (mutation.type === "multiImage") {
        if (state.app.pinSection.multiImageLength) {
          this.updateMultiImage();
        } else {
          this.stopMultiImage();
        }
      }
    })
  }

  /**
   * Options
   * @param options
   */
  setupOptions(options) {
    if (options.autoPlay) {
      this.options.autoPlay = true;
    }
  }

  setupSections() {
    //homeと差別化
    const home = document.getElementById("home");
    if(home){
      const homeWrapper = home.querySelector(".home-wrapper");
      var parentElem = document.querySelector("main section");
      while (parentElem.firstChild) {
        parentElem.parentNode.insertBefore(parentElem.firstChild, parentElem);
      }
        // 空の親要素を削除
      parentElem.parentNode.removeChild(parentElem);

      //　footerの位置をmain内に移動
      document.getElementById("shopify-section-footer").remove()
      const footer = document.querySelector(".pinfooter");
      if(footer && homeWrapper) homeWrapper.appendChild(footer);
    }else{

      //　ドットタグの位置をheader直下に移動
      const side_nav = document.querySelector(".l-sidenavi");
      const header     = document.getElementById("shopify-section-header");
      document.body.insertBefore(side_nav,header.nextSibling);

      document.getElementById("shopify-section-footer").remove()
      const main = document.querySelector("#main");
      const footer = document.querySelector(".pinfooter");
      if(footer)main.appendChild(footer);
    }

    let _sections = this.getSections();
    _each(_sections, (elm, index) => {
      elm.id = "section" + index;
      if (Common.isTabletSize()) {
        this.removeElm(elm.querySelectorAll(".pc"));
      } else {
        this.removeElm(elm.querySelectorAll(".sp"));
      }
      let _tabsections = elm.querySelectorAll(".js-tabsection");
      _each(_tabsections, (tabsection) => {
        tabsection.setAttribute("data-to", index);
      });
    });
  }

  removeElm(elms) {
    _each(elms, (elm) => {
      elm.remove();
    });
  }

  // トリガー設定 ===============
  setupScrollTriggers() {
    const sections = this.getSections();
    this.sectionLength = sections.length;
    this.triggers = [];
    store.dispatch("setupPinSection", {
      length: sections.length
    });
  }

  getSections() {
    let sections = [...document.querySelectorAll(".js-section")];
    let result = sections;
    _each(sections, (section, index) => {
      let onlySp = !!section.getAttribute("data-onlysp");
      if (onlySp && !Common.isTabletSize()) {
        result = _pullAt(sections, index);
      }
    });
    return sections;
  }
  getFpSections() {
    const sections = Array.from(document.querySelectorAll(".fp-section"));
    return sections;
  }

  // Multi Image Section =============
  updateMultiImage() {
    if (this.multiImageTween) {
      this.stopMultiImage();
    }
    this.changeMultiImage();
  }

  changeMultiImage() {
    let current = store.state.app.pinSection.multiImageCount;
    const total = store.state.app.pinSection.multiImageLength;
    this.execChangeMultiImage(current);
    let obj = {
      time: 0
    };
    this.multiImageTween = gsap.to(obj, {
      time: 1,
      duration: 5,
      ease: Linear.easeNone,
      onUpdate: () => {
        store.dispatch("multiImageTime", {
          time: obj.time
        });
      },
      onComplete: () => {
        let next = current + 1;
        if (next >= total) {
          next = 0;
        }
        store.dispatch("multiImage", {
          length: total,
          count: next
        });
      }
    });
  }

  // 表示情報を差し替える
  execChangeMultiImage(next) {
    const section = store.state.app.pinSection.nextSection;
    const sections = section.querySelectorAll(".js-multiimage");
    const nextSection = sections[next];

    if (!nextSection) {
      this.stopMultiImage();
      return;
    }
    _each(sections, (elm) => {
      elm.classList.add("hide");
    });

    store.dispatch("beforeMultiImageChange", {
      num: next,
      color: nextSection.classList.contains("white") ? "white" : ""
    });
    nextSection.classList.remove("hide");
    if (nextSection.classList.contains("js-multiimage")) {
      const els = nextSection.querySelectorAll("a, button, input, textarea, select, details, summary");
      if (els && els.length) {
        els[0].focus();
      }
    }
  }

  stopMultiImage() {
    if (this.multiImageTween) {
      this.multiImageTween.kill();
      this.multiImageTween = null;
    }
  }

  // Video Photo Effects =============
  // Photo effect
  photoSectionPlay(elm) {
    if (!store.state.app.isHome) {
      return;
    }

    const vimeoSection = elm.querySelector(".js-vimeo-section");
    const image = elm.querySelector(".section__background img");
    if (elm && !vimeoSection && image) {
      this.photoSectionTween = gsap.fromTo(
        image,
        {
          scale: 1.05
        },
        {
          duration: 5,
          delay: 0.4,
          scale: 1.0,
          ease: Linear.easeNone
        }
      );
    }
  }

  // Vimeo Play
  vimeoSectionPlay(elm) {
    if (this.vimeoPlayTween) {
      this.vimeoPlayTween.kill();
      this.vimeoPlayTween = null;
    }
    const vimeoSections = elm.querySelectorAll(".js-vimeo-section");

    if (elm && vimeoSections.length) {
      vimeoSections.forEach((_vimeoSection) => {
        const player = this.vimeoSection.getVimeoPlayer(_vimeoSection);
        const parrentSection = _vimeoSection?.closest(".js-section-background");
        const btn = parrentSection?.querySelector(".js-btn-play-pause");
        const pressed = btn?.getAttribute('aria-pressed');
        if (pressed === 'true') {
          return
        }

        if (player) {
          player.setMuted(true);
          let promise = player.pause();
          promise.then(() => {
            // パフォーマンス向上のためアニメーション中は動画再生しないようにする
            this.vimeoPlayTween = gsap.delayedCall(0.9, () => {
              promise = player.play();
            });

            if (btn && player) {
              btn.addEventListener("click", (e) => {
                if (btn.hasAttribute('data-is-pressing')) return

                e.preventDefault();

                btn.setAttribute('data-is-pressing', '')

                requestAnimationFrame(() => {
                  const pressed = btn.getAttribute('aria-pressed')
                  if (pressed === 'false') {
                    player?.pause();
                    this.multiImageTween?.pause();
                  } else {
                    player?.play();
                    this.multiImageTween?.play();
                  }

                  btn.setAttribute('aria-pressed', pressed === 'false')
                  btn.innerHTML = pressed === 'false' ? 'Play Video' : 'Pause Video'

                  btn.removeAttribute('data-is-pressing')
                })
              });
            }

          });
        }
      });
      /*const player = this.vimeoSection.getVimeoPlayer(vimeoSection);
      if (player) {
        player.setMuted(true);
        let promise = player.pause();
        promise.then(() => {
          // パフォーマンス向上のためアニメーション中は動画再生しないようにする
          this.vimeoPlayTween = gsap.delayedCall(0.9, () => {
            promise = player.play();
          });
        });
      }*/
    }
  }
  // Vimeo Pause
  vimeoSectionPause(elm) {
    const vimeoSections = elm.querySelectorAll(".js-vimeo-section");
    if (elm && vimeoSections.length) {
      vimeoSections.forEach((_vimeoSection) => {
        const player = this.vimeoSection.getVimeoPlayer(_vimeoSection);
        if (player) {
          player.setMuted(true);
          let promise = player.pause();
          promise.then(() => {});
        }
      });
    }
  }
}
